import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StatusCode from "../../../util/StatusCode";
import TableHorizontal from "../../misc/TableHorizontal";
import PrimarySchoolModal from "./PrimarySchoolModal";
import * as Helper from '../../../util/Helper';
import * as PrimarySchoolTypes from '../../../util/PrimarySchoolUtil';
import { CoreDisplayableRef } from "../../../models/CoreModels";
import { DeleteUserPrimarySchool, GetUserSchoolUnits, ResetPrimarySchoolSaveStatus, SetUserPrimarySchool, UserSchoolUnitsRequestState } from "../../../store/UserAction";
import { CoreUserDuty, CoreUserEnrollment } from "../../../models/CoreUser";
import { CorePrimarySchoolStatus } from "../../../models/CoreUser";
import { FlowSyncAccessPolicy, isAuthorized } from "../../../models/FlowSyncAccessPolicy";
import * as SchoolTypeUtil from '../../../util/SchoolTypeUtil';
import ActiveToggle from "../../misc/ActiveToggle";
import { EmptyRequestState } from "../../../models/RequestState";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../store/store";



type PrimarySchoolOption = {
    primarySchoolStatus: CorePrimarySchoolStatus
    schoolUnit: CoreDisplayableRef
}

const getPrimarySchoolOptions = (userSchoolUnits: UserSchoolUnitsRequestState): PrimarySchoolOption[] => {
    const uniqueOptions: Map<string, PrimarySchoolOption> = new Map<string, PrimarySchoolOption>();

    if (userSchoolUnits.code !== StatusCode.COMPLETE) {
        return Array.from(uniqueOptions.values());
    }

    const memberships: (CoreUserDuty | CoreUserEnrollment)[] = Helper.ensureArray(userSchoolUnits.data?.duties)
    const enrollments = Helper.ensureArray(userSchoolUnits.data?.enrollments);

    memberships.concat(enrollments).forEach(membership => {
        if (!membership.primarySchoolStatus || membership.primarySchoolStatus.status === PrimarySchoolTypes.Types.DEFAULT) {
            return;
        }

        if (!membership.schoolUnit || uniqueOptions.has(membership.schoolUnit.id)) {
            return;
        }

        uniqueOptions.set(membership.schoolUnit.id, {
            primarySchoolStatus: membership.primarySchoolStatus,
            schoolUnit: membership.schoolUnit,
        });
    });

    return Array.from(uniqueOptions.values());
}

const UserViewSchoolUnits = () => {
    const { t } = useTranslation(['common', 'schoolAdministration']);

    const { userId } = useParams();
    const dispatch = useDispatch();
    const userSchoolUnits = useSelector<RootState, UserSchoolUnitsRequestState>(state => state.users.userSchoolUnits);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const primarySchoolSaveStatus = useSelector<RootState, EmptyRequestState>(state => state.users.primarySchool);

    const [open, setOpen] = useState(false);
    const [primarySchool, setPrimarySchool] = useState<PrimarySchoolOption | undefined>(undefined);
    const [selectableSchoolUnits, setSelectableSchoolUnits] = useState<PrimarySchoolOption[] | []>([]);

    useEffect(() => {
        if (userId) {
            GetUserSchoolUnits(userId, false, {})(dispatch);
        }
    }, [dispatch, userId]);

    useEffect(() => {
        if (primarySchoolSaveStatus.code === StatusCode.COMPLETE && userId) {
            setTimeout(() => {
                GetUserSchoolUnits(userId, true, { ...userSchoolUnits.filter })(dispatch);
                setOpen(false);
                ResetPrimarySchoolSaveStatus()(dispatch);
            }, 1500);
        }
    });

    useEffect(() => {
        if (userSchoolUnits.code === StatusCode.COMPLETE) {
            const selectableSchoolUnitsList = getPrimarySchoolOptions(userSchoolUnits)
                .sort((a, b) => a.schoolUnit.title! > b.schoolUnit.title! ? 1 : -1);

            setSelectableSchoolUnits(selectableSchoolUnitsList);

            const selectedPrimary = selectableSchoolUnitsList.find(x => x.primarySchoolStatus.overriddenPrimary) ||
                selectableSchoolUnitsList.find(x => x.primarySchoolStatus.status === PrimarySchoolTypes.Types.DEFAULTPRIMARY);
            if (selectedPrimary) {
                setPrimarySchool(selectedPrimary);
            }
        }
    }, [userSchoolUnits]);

    const updateOnlyActive = (value: boolean) => {
        if (userId) {
            GetUserSchoolUnits(userId, true, { ...userSchoolUnits.filter, onlyActive: value })(dispatch);
        };
    };

    const updatePrimarySchool = async (updatedSchool: string) => {
        if (userId) {
            SetUserPrimarySchool(userId, updatedSchool)(dispatch);
        };
    };

    const deleteOverridenPrimarySchool = async () => {
        if (userId) {
            DeleteUserPrimarySchool(userId)(dispatch);
        }
    }

    const closeModal = (): void => {
        setOpen(false);
        ResetPrimarySchoolSaveStatus()(dispatch);
    }

    const renderModal = () => (
        <PrimarySchoolModal
            isOpen={open}
            toggleModal={() => closeModal()}
            saveStatus={primarySchoolSaveStatus.code}
            deleteOverridenPrimarySchool={() => deleteOverridenPrimarySchool()}
            items={selectableSchoolUnits}
            primarySchool={primarySchool}
            getIcons={primarySchoolStatus => getIcons(primarySchoolStatus)}
            update={(updatedSchool) => updatePrimarySchool(updatedSchool)}
            disabled={!isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_INFORMATION)}
        />
    );

    const getIcons = (primarySchoolStatus: CorePrimarySchoolStatus) => {
        let icon = [];
        if (primarySchoolStatus?.overriddenPrimary) {
            icon.push(<FontAwesomeIcon key='green' className="me-2" title={t('primarySchool.selected', { ns: 'schoolAdministration' })} color="orange" icon="crown" />);
        }
        if (primarySchoolStatus?.status === PrimarySchoolTypes.Types.DEFAULTPRIMARY) {
            icon.push(<FontAwesomeIcon key='grey' className="me-2" title={t('primarySchool.estimated', { ns: 'schoolAdministration' })} color="grey" icon="check" />);
        }
        return icon;
    }

    const iconWithPen = (schoolUnitId: string, first: boolean, primarySchoolStatus?: CorePrimarySchoolStatus) => {
        if (primarySchoolStatus) {
            const icons = getIcons(primarySchoolStatus);
            const allowedEdit = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_INFORMATION);
            const displayPen = icons.length >= 1 && selectableSchoolUnits.length > 1 && primarySchool?.schoolUnit.id === schoolUnitId && first && allowedEdit;
            return (<>
                {icons}
                {displayPen && <FontAwesomeIcon className="c-pointer" title={t('primarySchool.edit', { ns: 'schoolAdministration' })} icon="pen" onClick={() => setOpen(true)} />}
            </>);
        }
        return '-';
    }

    const hasData = () => {
        if (userSchoolUnits.code === StatusCode.COMPLETE) {
            return userSchoolUnits.data;
        }
        return null;
    }

    const renderEnrollmentSchoolUnits = () => {
        const handled: string[] = [];
        const isHandled = (schoolUnitId: string) => handled.some(s => s === schoolUnitId);

        const enrollmentSchools = hasData()?.enrollments
            .filter(x => x.schoolUnit !== undefined)
            .map(x => {
                var first = !isHandled(x.schoolUnit!.id);
                if (x.primarySchoolStatus) {
                    handled.push(x.schoolUnit!.id)
                }
                return ({
                    id: x.toString(),
                    data: [
                        { value: <Link to={`/schoolunit/${x.schoolUnit!.id}`} > {x.schoolUnit!.title} </Link> },
                        { value: SchoolTypeUtil.getTranslationKeys(x.schoolType?.id ?? 0).map(k => t(k, { ns: 'common' })).join(', ') },
                        { value: x.schoolYear },
                        { value: x.educationCode },
                        { value: x.active !== undefined ? Helper.active(x.active) : '-' },
                        { value: Helper.formatDateOnly(x.startDate) },
                        { value: Helper.formatDateOnly(x.endDate) },
                        { value: iconWithPen(x.schoolUnit!.id, first, x.primarySchoolStatus) },
                    ]
                })
            }) ?? [];
        return (
            <TableHorizontal
                header={[{ value: t('enrollmentSchoolUnitTableHeaders.enrollment', { ns: 'schoolAdministration' }) }, { value: t('enrollmentSchoolUnitTableHeaders.formOfEducation', { ns: 'schoolAdministration' }) }, { value: t('enrollmentSchoolUnitTableHeaders.schoolYear', { ns: 'schoolAdministration' }) }, { value: t('enrollmentSchoolUnitTableHeaders.educationCode', { ns: 'schoolAdministration' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('genericTableHeaders.startDate', { ns: 'common' }) }, { value: t('genericTableHeaders.endDate', { ns: 'common' }) }, { value: <>{t('primarySchool.primary', { ns: 'schoolAdministration' })} <a href="https://flowsync.se/help/flowsync/primary-school" target="_blank" rel="noreferrer" > <FontAwesomeIcon className="documentation-icon small" title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a> </>, dataLabel: t('primarySchool.primary', { ns: 'schoolAdministration' }) }]}
                info={enrollmentSchools}
                displayName={t('enrollmentSchoolUnitTableHeaders.enrollments', { ns: 'schoolAdministration' })}
                searching={userSchoolUnits.code === StatusCode.PENDING}
                error={userSchoolUnits.code === StatusCode.ERROR}
                noResMsg={t('enrollmentSchoolUnitTableHeaders.noResult', { ns: 'schoolAdministration' })}
            />
        );
    };

    const renderDutySchoolUnits = () => {
        const handled: string[] = [];
        const isHandled = (schoolUnitId: string) => handled.some(s => s === schoolUnitId);

        const dutySchools = hasData()?.duties
            .filter(x => x.schoolUnit !== undefined)
            .map(x => {
                var first = !isHandled(x.schoolUnit!.id);
                if (x.primarySchoolStatus) {
                    handled.push(x.schoolUnit!.id)
                }
                return ({
                    id: x.toString(),
                    data: [
                        { value: <Link to={`/schoolunit/${x.schoolUnit!.id}`} > {x.schoolUnit!.title} </Link> },
                        { value: x.dutyRole ? t(`dutyRoleTypes.${x.dutyRole.id}`, { ns: 'schoolAdministration' }) : '-' },
                        { value: x.signature },
                        { value: x.dutyPercent && `${x.dutyPercent} %` },
                        { value: x.active !== undefined ? Helper.active(x.active) : '-' },
                        { value: Helper.formatDateOnly(x.startDate) },
                        { value: Helper.formatDateOnly(x.endDate) },
                        { value: iconWithPen(x.schoolUnit!.id, first, x.primarySchoolStatus) },
                    ]
                })
            }) ?? [];
        return (
            <TableHorizontal
                header={[{ value: t('dutySchoolUnitTableHeaders.duty', { ns: 'schoolAdministration' }) }, { value: t('commonTableHeaders.role', { ns: 'schoolAdministration' }) }, { value: t('dutySchoolUnitTableHeaders.signature', { ns: 'schoolAdministration' }) }, { value: t('dutySchoolUnitTableHeaders.dutyPercent', { ns: 'schoolAdministration' }) }, { value: t('genericTableHeaders.active', { ns: 'common' }) }, { value: t('genericTableHeaders.startDate', { ns: 'common' }) }, { value: t('genericTableHeaders.endDate', { ns: 'common' }) }, { value: <>{t('primarySchool.primary', { ns: 'schoolAdministration' })} <a href="https://flowsync.se/help/flowsync/primary-school" target="_blank" rel="noreferrer" > <FontAwesomeIcon className="documentation-icon small" title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a> </>, dataLabel: t('primarySchool.primary', { ns: 'schoolAdministration' }) }]}
                info={dutySchools}
                displayName={t('dutySchoolUnitTableHeaders.duties', { ns: 'schoolAdministration' })}
                searching={userSchoolUnits.code === StatusCode.PENDING}
                error={userSchoolUnits.code === StatusCode.ERROR}
                noResMsg={t('dutySchoolUnitTableHeaders.noResult', { ns: 'schoolAdministration' })}
            />
        );
    };

    const renderPlacements = () => {
        const handled: string[] = [];
        const isHandled = (schoolUnitId: string) => handled.some(s => s === schoolUnitId);

        const placementSchools = hasData()?.placements
            .filter(x => x.schoolUnit !== undefined)
            .map(x => {
                var first = !isHandled(x.schoolUnit!.id);
                if (x.primarySchoolStatus) {
                    handled.push(x.schoolUnit!.id)
                }
                return ({
                    id: x.toString(),
                    data: [
                        { value: <Link to={`/schoolunit/${x.schoolUnit!.id}`} > {x.schoolUnit!.title} </Link> },
                        { value: SchoolTypeUtil.getTranslationKeys(x.schoolType?.id ?? 0).map(k => t(k, { ns: 'common' })).join(', ') },
                        { value: x.group ? <Link to={`/group/${x.group.id}`} > {x.group.title} </Link> : '-' },
                        { value: t(`placementReason.${x.reason}`, {ns: 'schoolAdministration' })},
                        { value: x.maxWeeklyScheduledHours },
                        { value: x.active !== undefined ? Helper.active(x.active) : '-' },
                        { value: Helper.formatDateOnly(x.startDate) },
                        { value: Helper.formatDateOnly(x.endDate) },
                        { value: iconWithPen(x.schoolUnit!.id, first, x.primarySchoolStatus) },
                    ]
                })
            }) ?? [];
        return (
            <TableHorizontal
                header={[
                    { value: t('placementSchoolUnitTableHeaders.placement', { ns: 'schoolAdministration' }) },
                    { value: t('placementSchoolUnitTableHeaders.schoolType', { ns: 'schoolAdministration' }) },
                    { value: t('placementSchoolUnitTableHeaders.group', { ns: 'schoolAdministration' }) },
                    { value: t('placementSchoolUnitTableHeaders.reason', { ns: 'schoolAdministration' }) },
                    { value: t('placementSchoolUnitTableHeaders.maxWeeklyScheduledHours', { ns: 'schoolAdministration' }) },
                    { value: t('genericTableHeaders.active', { ns: 'common' }) },
                    { value: t('genericTableHeaders.startDate', { ns: 'common' }) },
                    { value: t('genericTableHeaders.endDate', { ns: 'common' }) },
                    { value: <>{t('primarySchool.primary', { ns: 'schoolAdministration' })} <a href="https://flowsync.se/help/flowsync/primary-school" target="_blank" rel="noreferrer" > <FontAwesomeIcon className="documentation-icon small" title={t('showHelp', { ns: 'common' })} icon="question-circle" /></a> </>, dataLabel: t('primarySchool.primary', { ns: 'schoolAdministration' }) }]}
                info={placementSchools}
                displayName={t('placementSchoolUnitTableHeaders.placements', { ns: 'schoolAdministration' })}
                searching={userSchoolUnits.code === StatusCode.PENDING}
                error={userSchoolUnits.code === StatusCode.ERROR}
                noResMsg={t('placementSchoolUnitTableHeaders.noResult', { ns: 'schoolAdministration' })}
            />
        );
    }

    return (<>
        <div className="header_member_table large">{t('schoolConnections', { ns: 'schoolAdministration' })}
            <span className="right">
                <ActiveToggle
                    onlyActive={userSchoolUnits.filter.onlyActive ?? true}
                    update={updateOnlyActive}
                    disabled={userSchoolUnits.code === StatusCode.PENDING}
                />
            </span>
        </div>
        {renderDutySchoolUnits()}
        {renderEnrollmentSchoolUnits()}
        {renderPlacements()}
        {open && renderModal()}
    </>)
}

export default UserViewSchoolUnits;
